import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

// Music track kinds - support both standard (36787) and legacy (30999)
export const MUSIC_TRACK_KIND = 36787; // Standard kind for new uploads
export const MUSIC_TRACK_KINDS = [36787, 30999]; // Query both for compatibility

export interface MusicTrack {
  event: NostrEvent;
  id: string;
  title: string;
  artist: string;
  url: string;
  hash?: string;
  mimeType: string;
  duration: number;
  album?: string;
  genres: string[];
  coverImage?: string;
  videoUrl?: string;
  publishedAt: number;
  fileSize?: number;
  description: string;
  isAiGenerated: boolean;
  trackNumber?: number;
  released?: string;
  format?: string;
  bitrate?: string;
  language?: string;
  explicit: boolean;
}

function parseMusicTrack(event: NostrEvent): MusicTrack | null {
  const title = event.tags.find(([name]) => name === 'title')?.[1];
  const artist = event.tags.find(([name]) => name === 'artist')?.[1];
  const url = event.tags.find(([name]) => name === 'url')?.[1];
  const hash = event.tags.find(([name]) => name === 'x')?.[1];
  const mimeType = event.tags.find(([name]) => name === 'm')?.[1];
  const format = event.tags.find(([name]) => name === 'format')?.[1];

  // Only require title and url - hash is optional for kind 36787
  if (!title || !url) {
    return null;
  }

  const durationStr = event.tags.find(([name]) => name === 'duration')?.[1];
  const duration = durationStr ? parseInt(durationStr) : 0;

  const album = event.tags.find(([name]) => name === 'album')?.[1];
  // Filter out the required "music" tag from genres display
  const genres = event.tags
    .filter(([name]) => name === 't')
    .map(([_, genre]) => genre)
    .filter(g => g !== 'music');
  const coverImage = event.tags.find(([name]) => name === 'image')?.[1];
  const videoUrl = event.tags.find(([name]) => name === 'video')?.[1];
  
  // Support both published_at (legacy) and released (new standard)
  const publishedAtStr = event.tags.find(([name]) => name === 'published_at')?.[1];
  const releasedStr = event.tags.find(([name]) => name === 'released')?.[1];
  
  const fileSizeStr = event.tags.find(([name]) => name === 'size')?.[1];
  const aiTag = event.tags.find(([name]) => name === 'ai')?.[1];
  const isAiGenerated = aiTag === 'true';
  
  // New standard fields
  const trackNumberStr = event.tags.find(([name]) => name === 'track_number')?.[1];
  const bitrate = event.tags.find(([name]) => name === 'bitrate')?.[1];
  const language = event.tags.find(([name]) => name === 'language')?.[1];
  const explicitTag = event.tags.find(([name]) => name === 'explicit')?.[1];

  // Parse published date - handle both unix timestamp and ISO date
  let publishedAt = event.created_at;
  if (publishedAtStr) {
    publishedAt = parseInt(publishedAtStr);
  } else if (releasedStr) {
    const parsed = Date.parse(releasedStr);
    if (!isNaN(parsed)) {
      publishedAt = Math.floor(parsed / 1000);
    }
  }

  return {
    event,
    id: event.id,
    title,
    artist: artist || 'Unknown Artist',
    url,
    hash,
    mimeType: mimeType || format || 'audio/mpeg',
    duration: isNaN(duration) ? 0 : duration,
    album,
    genres,
    coverImage,
    videoUrl,
    publishedAt,
    fileSize: fileSizeStr ? parseInt(fileSizeStr) : undefined,
    description: event.content,
    isAiGenerated,
    trackNumber: trackNumberStr ? parseInt(trackNumberStr) : undefined,
    released: releasedStr,
    format,
    bitrate,
    language,
    explicit: explicitTag === 'true',
  };
}

export function useMusicTracks(limit: number = 100) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['music-tracks', limit],
    queryFn: async () => {
      // Query both standard (36787) and legacy (30999) kinds
      const events = await nostr.query([{
        kinds: MUSIC_TRACK_KINDS,
        limit,
      }]);

      const tracks = events
        .map(parseMusicTrack)
        .filter((track): track is MusicTrack => track !== null)
        .sort((a, b) => b.publishedAt - a.publishedAt);

      return tracks;
    },
    staleTime: 1000 * 30, // 30 seconds - refresh more often
    refetchOnWindowFocus: true,
  });
}

export function useArtistTracks(pubkey: string, limit: number = 50) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['artist-tracks', pubkey, limit],
    queryFn: async () => {
      const events = await nostr.query([{
        kinds: MUSIC_TRACK_KINDS,
        authors: [pubkey],
        limit,
      }]);

      const tracks = events
        .map(parseMusicTrack)
        .filter((track): track is MusicTrack => track !== null)
        .sort((a, b) => b.publishedAt - a.publishedAt);

      return tracks;
    },
    enabled: !!pubkey,
    staleTime: 1000 * 30,
  });
}

export function useTrackById(eventId: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['track', eventId],
    queryFn: async () => {
      const events = await nostr.query([{
        kinds: MUSIC_TRACK_KINDS,
        ids: [eventId],
        limit: 1,
      }]);

      if (events.length === 0) return null;
      return parseMusicTrack(events[0]);
    },
    enabled: !!eventId,
    staleTime: 1000 * 60 * 10,
  });
}

export function useTracksByGenre(genre: string, limit: number = 50) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['tracks-by-genre', genre, limit],
    queryFn: async () => {
      const events = await nostr.query([{
        kinds: MUSIC_TRACK_KINDS,
        '#t': [genre.toLowerCase()],
        limit,
      }]);

      const tracks = events
        .map(parseMusicTrack)
        .filter((track): track is MusicTrack => track !== null)
        .sort((a, b) => b.publishedAt - a.publishedAt);

      return tracks;
    },
    enabled: !!genre,
    staleTime: 1000 * 30,
  });
}
